import {
    AttachmentType,
    deleteLibraryDocument,
    getDocumentCoi,
    getDocumentExtractedFields,
    getDocumentRelated,
    getLibraryDocument,
    ILibraryDocument,
    ILibraryDocumentListItem,
    readScopesEnabled,
} from "@/api";
import { TERMINAL_EXTRACTION_STATUSES } from "@/api/documents/documents.types";
import { ChatSheet } from "@/components/assistant-ui/chat-sheet";
import { CreateScopeDialog } from "@/components/frontend/dialogs/create-scope/CreateScopeDialog";
import { DocumentPickerValue } from "@/components/shared/document-picker";
import { MoveToFolderModal } from "@/components/shared/move-to-folder";
import { Tooltip } from "@/components/shared/Tooltip";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import { trackDocumentViewed } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { isForeignSharedSubject } from "@/pages/frontend/documents/utils/folderTreeUtils";
import { sharedKnowledgeBaseLabel } from "@/pages/frontend/documents/utils/sharedKnowledgeBaseCopy";
import { libraryDocumentToListItem } from "@/utils/library";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDialogs } from "@toolpad/core";
import { formatDistanceToNowStrict } from "date-fns";
import {
    BookOpen,
    Download,
    Eye,
    FileText,
    Folder,
    FolderInput,
    MessageSquare,
    Pencil,
    ScanSearch,
    Trash2,
    TriangleAlert,
    X,
} from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { DeleteDocumentDialog } from "./DeleteDocumentDialog";
import { ExtractedFieldsSection } from "./extraction/ExtractedFieldsSection";
import { PdfSourceModal } from "./extraction/PdfSourceModal";
import { RelatedComparisonsSection } from "./RelatedComparisonsSection";
import { RelatedScopesSection } from "./RelatedScopesSection";
import { RenameDocumentDialog } from "./RenameDocumentDialog";
import { usePanelEscape } from "./usePanelEscape";

interface DocumentDetailPanelProps {
    open: boolean;
    onClose: () => void;
    documentId: number | null;
    isMobile: boolean;
    /** Current visible PDF page (1-indexed). Optional; forwarded to custom-field forms. */
    currentPage?: number;
    /** Total pages in the viewed PDF. Optional; used to bound page_number inputs. */
    totalPages?: number;
}

// Cross-slice contract: this type is the wire between Slice 7 (popover
// callback writes it) and Slice 8 (modal reads it). Slice 8 fetches
// `highlight_areas` itself via Slice 2's lazy endpoint; those coordinates
// are NOT part of this trigger payload.
export type PdfModalArgs = {
    documentId: number;
    /**
     * `null` for document-level deep links with no backing property
     * (e.g., the COI "Certificate Details" page pill). The modal skips the
     * highlight-areas fetch in that case.
     */
    propertyId: number | null;
    /** 1-indexed; null when the source has no page reference. */
    pageNumber: number | null;
    fieldName: string;
    /** Verbatim quote text or null fallback. */
    source: string | null;
};

type PanelDialog = "rename" | "move" | "delete" | "chat";

const TAG_PALETTE = {
    industry: "bg-primary/10 text-primary",
    carrier: "bg-muted text-foreground",
} as const;

/* ─── Helpers ────────────────────────────────────────────────────────── */

const toListItem = (doc: ILibraryDocument): ILibraryDocumentListItem => ({
    id: doc.id,
    document_name: doc.document_name,
    library_folder_id: doc.library_folder_id,
    industry_id: doc.industry?.id ?? null,
    carrier_id: doc.carrier?.id ?? null,
    client_id: doc.client?.id ?? null,
    industry_name: doc.industry?.name ?? null,
    carrier_name: doc.carrier?.carrier_name ?? null,
    client_name: doc.client?.name ?? null,
    organization_name: null,
    textract_status: doc.textract_status,
    page_count: doc.page_count,
    file_size_bytes: doc.file_size_bytes,
    created_at: doc.created_at,
});

const toPickerValue = (
    doc: ILibraryDocument,
    objectName: string
): DocumentPickerValue => ({
    source: "library" as const,
    objectName,
    libraryDocumentId: doc.id,
    title: doc.document_name,
    folderId: doc.library_folder_id,
    folderName: doc.folder?.name ?? null,
});

/* ─── Main Component ─────────────────────────────────────────────────── */

export const DocumentDetailPanel = ({
    open,
    onClose,
    documentId,
    isMobile,
    currentPage,
    totalPages,
}: DocumentDetailPanelProps) => {
    const dialogs = useDialogs();
    const queryClient = useQueryClient();
    const { organization, settings: organizationSettings } = useOrganization();
    const scopesEnabled = readScopesEnabled(organizationSettings);

    // The panel survives documentId changes, so open dialogs are keyed to the
    // document they were opened for and dropped whenever documentId changes,
    // even when the same document is selected again.
    const [openDialog, setOpenDialog] = useState<{
        kind: PanelDialog;
        documentId: number;
    } | null>(null);
    // Slice 7 wires setPdfModal via the SourceQuotePopover "Open in document"
    // callback drilled through ExtractedFieldsSection → ExtractedFieldRow.
    // Slice 8 conditionally renders <PdfSourceModal {...pdfModal} /> when non-null.
    const [pdfModal, setPdfModal] = useState<PdfModalArgs | null>(null);
    const [dialogsDocumentId, setDialogsDocumentId] = useState(documentId);
    if (dialogsDocumentId !== documentId) {
        setDialogsDocumentId(documentId);
        setOpenDialog(null);
        setPdfModal(null);
    }
    // Slice 6 will drive this from inline-edit state inside ExtractedFieldRow.
    const [isInlineEditing] = useState(false);

    const panelRef = useRef<HTMLDivElement>(null);

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: [
            "frontend-library-document-related",
            documentId,
            { scopesEnabled },
        ],
        queryFn: () => getDocumentRelated(documentId!),
        enabled: open && documentId !== null,
        placeholderData: (previousData, previousQuery) =>
            previousQuery?.queryKey[1] === documentId
                ? previousData
                : undefined,
        refetchInterval: (query) => {
            const status = query.state.data?.document.textract_status;
            if (status !== "processed" && status !== "failed") return 5000;
            return false;
        },
    });

    const doc = data?.document;
    const isCurrentDocument = !!doc && doc.id === documentId;
    const isForeign = isCurrentDocument && isForeignSharedSubject(doc);
    const isConfirmedOwn = isCurrentDocument && !isForeignSharedSubject(doc);
    const scopes = data?.scopes ?? [];
    const comparisons = data?.comparisons ?? [];
    const isKnowledgeBase = doc?.folder?.is_knowledge_base ?? false;
    // Spreadsheets (.xlsx/.csv) have no in-app preview, so the primary file
    // action downloads rather than opens (matching the chat viewer's
    // no-preview route). Spreadsheet-POSITIVE (`file_type in {xlsx,csv}`),
    // NEVER `!== "pdf"`: a legacy `null`-file_type doc stays on the "View" path.
    const isSpreadsheet = doc?.file_type === "xlsx" || doc?.file_type === "csv";
    // Scope/clause analysis is a contract-PDF flow: it doesn't exist (and can't
    // be created) for spreadsheets, and KB documents already hide it. Gates both
    // the "Run Scope" button and the related scopes/comparisons section below.
    const supportsScopeAnalysis =
        isConfirmedOwn && !isKnowledgeBase && !isSpreadsheet && scopesEnabled;

    // Dep on doc?.id (not documentId) so capture waits for the related-doc query to resolve.
    useEffect(() => {
        if (!open || !doc) return;
        trackDocumentViewed({
            document_id: doc.id,
            folder_id: doc.library_folder_id,
            is_knowledge_base:
                doc.folder?.is_knowledge_base ?? isForeignSharedSubject(doc),
            textract_status: doc.textract_status,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, doc?.id]);
    const isDocumentProcessed = doc?.textract_status === "processed";
    const isChatEnabled = organizationSettings?.ai_chat_enabled ?? false;

    const isPdfModalOpen =
        pdfModal !== null &&
        isConfirmedOwn &&
        pdfModal.documentId === documentId;
    const isDialogOpen = (kind: PanelDialog) =>
        isConfirmedOwn &&
        openDialog?.kind === kind &&
        openDialog.documentId === documentId;
    const openDialogFor = (kind: PanelDialog) => {
        if (documentId !== null) setOpenDialog({ kind, documentId });
    };
    const closeDialog = () => setOpenDialog(null);

    // `!isMobile`: on mobile the panel renders inside a Sheet whose Dialog
    // already handles Esc → close natively. Attaching this hook on top would
    // double-fire `onClose` and bypass the `isInlineEditing` guard (the
    // Sheet's native Esc is unaware of inline-edit state). Mobile inline
    // edits must stop Esc propagation at the input that owns it.
    usePanelEscape({
        enabled: open && !isMobile,
        onClose,
        isModalOpen: isPdfModalOpen,
        isInlineEditing,
        targetRef: panelRef,
    });

    const fieldsQuery = useQuery({
        queryKey: ["frontend-document-extracted-fields", documentId],
        queryFn: () => getDocumentExtractedFields(documentId!),
        enabled:
            open &&
            documentId !== null &&
            isDocumentProcessed &&
            isConfirmedOwn,
        refetchInterval: (q) => {
            const s = q.state.data?.field_extraction_status;
            return s && TERMINAL_EXTRACTION_STATUSES.has(s) ? false : 3000;
        },
    });

    const fieldStatus = fieldsQuery.data?.field_extraction_status;
    const isExtractionTerminal =
        fieldStatus !== undefined &&
        TERMINAL_EXTRACTION_STATUSES.has(fieldStatus);

    const coiQuery = useQuery({
        queryKey: ["frontend-document-coi", documentId],
        queryFn: () => getDocumentCoi(documentId!),
        enabled:
            open &&
            documentId !== null &&
            isDocumentProcessed &&
            isConfirmedOwn,
        refetchInterval: isExtractionTerminal ? false : 3000,
    });

    const tags: { label: string; colorKey: keyof typeof TAG_PALETTE }[] = [];
    if (doc?.industry)
        tags.push({ label: doc.industry.name, colorKey: "industry" });
    if (doc?.carrier)
        tags.push({ label: doc.carrier.carrier_name, colorKey: "carrier" });

    /* ── Actions ──────────────────────────────────────────────────────── */

    const { mutateAsync: viewDocument, isPending: isViewing } = useMutation({
        mutationFn: () => getLibraryDocument(documentId!, true),
    });

    // Open the tab synchronously inside the click gesture, THEN point it at the
    // presigned URL once the on-click fetch resolves. `window.open(url)` after an
    // awaited round-trip lands outside the user-gesture window and is silently
    // popup-blocked in Safari/Firefox; opening a blank tab first is not.
    const handleViewDocument = () => {
        const win = window.open("", "_blank");
        if (!win) {
            toast.error(
                "Your browser blocked the new tab. Allow pop-ups and try again."
            );
            return;
        }
        win.opener = null;
        void viewDocument()
            .then((result) => {
                if (result.download_url) {
                    win.location.href = result.download_url;
                } else {
                    win.close();
                    toast.warning(
                        "View link is not available for this document"
                    );
                }
            })
            .catch(() => {
                win.close();
                toast.error("Failed to view document");
            });
    };

    const shownDocumentIdRef = useRef(documentId);
    useEffect(() => {
        shownDocumentIdRef.current = documentId;
    }, [documentId]);

    const { mutate: deleteDocument, isPending: isDeleting } = useMutation({
        mutationFn: (id: number) => deleteLibraryDocument(id),
        onSuccess: (_data, deletedId) => {
            queryClient.invalidateQueries({
                queryKey: ["frontend-library-list"],
            });
            queryClient.invalidateQueries({
                queryKey: ["library-documents"],
            });
            queryClient.invalidateQueries({
                queryKey: ["frontend-library-folders"],
            });
            toast.success("Document deleted successfully");
            if (shownDocumentIdRef.current !== deletedId) return;
            closeDialog();
            onClose();
        },
        onError: () => {
            toast.error("Failed to delete document");
        },
    });

    const handleRunScope = () => {
        const objectName = doc?.object_name;
        if (!doc || !isConfirmedOwn || !objectName) return;
        dialogs.open(CreateScopeDialog, {
            organizationId: organization.id,
            organizationIndustryId: organization.industry_id,
            initialDocument: toPickerValue(doc, objectName),
        });
    };

    const handleMoveComplete = () => {
        queryClient.invalidateQueries({
            queryKey: ["frontend-library-document-related", documentId],
        });
        closeDialog();
        onClose();
    };

    // `@container` makes the panel adapt to its own width via container
    // queries (see `@max-md:hidden` on action labels below) instead of
    // pinning a min-width. The library route mounts this panel in a
    // percentage-driven resizable column (33%–60%) that can shrink below
    // 480px; a min-width would push content past the parent's
    // `overflow-hidden` and clip it. Mobile width is handled by the Sheet.
    const panelContent = (
        <div
            ref={panelRef}
            tabIndex={-1}
            className="bg-background @container flex h-full flex-col outline-none"
        >
            <PanelHeader
                filename={doc?.document_name}
                isLoading={isLoading}
                onRename={() => openDialogFor("rename")}
                onClose={onClose}
                showRename={isConfirmedOwn}
            />

            <div className="flex flex-1 flex-col overflow-hidden">
                {/*
                  Sticky FieldsToolbar contract: the toolbar locks at
                  scrollport-top, but `overflow: auto` clips at the
                  padding-box edge, so any padding-top on this scroll
                  container becomes a strip where scrolling content is
                  visible above the locked toolbar. Keep the 16px top
                  breathing room on the inner wrapper instead so the
                  toolbar locks flush with the container's outer top.
                */}
                <div className="flex-1 overflow-y-auto px-5 pb-4">
                    <div className="pt-4">
                        {isLoading ? (
                            <LoadingSkeleton />
                        ) : isError ? (
                            <PanelErrorState onRetry={() => refetch()} />
                        ) : (
                            doc && (
                                <>
                                    <MetaStrip
                                        folderName={
                                            isForeign
                                                ? null
                                                : (doc.folder?.name ?? null)
                                        }
                                        isKnowledgeBase={
                                            doc.folder?.is_knowledge_base ??
                                            false
                                        }
                                        sharedBadgeLabel={
                                            isForeign
                                                ? sharedKnowledgeBaseLabel(
                                                      doc.shared_group_name
                                                  )
                                                : null
                                        }
                                        pageCount={doc.page_count}
                                        clientName={
                                            isForeign
                                                ? null
                                                : (doc.client?.name ?? null)
                                        }
                                        createdAt={doc.created_at}
                                        tags={tags}
                                    />

                                    <PanelActions
                                        supportsScopeAnalysis={
                                            supportsScopeAnalysis
                                        }
                                        isChatEnabled={
                                            isChatEnabled && isConfirmedOwn
                                        }
                                        canManage={isConfirmedOwn}
                                        isDocumentProcessed={
                                            isDocumentProcessed
                                        }
                                        isViewing={isViewing}
                                        isSpreadsheet={isSpreadsheet}
                                        onRunScope={handleRunScope}
                                        onAskPolly={() => openDialogFor("chat")}
                                        onView={handleViewDocument}
                                        onMove={() => openDialogFor("move")}
                                        onDelete={() => openDialogFor("delete")}
                                    />

                                    {isConfirmedOwn &&
                                        isDocumentProcessed &&
                                        // Spreadsheets carry no extracted fields
                                        // (field extraction is NOT_APPLICABLE);
                                        // gating here also keeps a spreadsheet's
                                        // "view source" from ever mounting the PDF
                                        // viewer via PdfSourceModal. Not
                                        // supportsScopeAnalysis: KB PDFs still show
                                        // fields.
                                        !isSpreadsheet &&
                                        (fieldsQuery.isLoading ||
                                            fieldsQuery.data ||
                                            fieldsQuery.isError) && (
                                            <>
                                                <div className="mb-2">
                                                    <ExtractedFieldsSection
                                                        documentId={documentId!}
                                                        data={fieldsQuery.data}
                                                        coi={
                                                            coiQuery.data ??
                                                            null
                                                        }
                                                        isLoading={
                                                            fieldsQuery.isLoading
                                                        }
                                                        isError={
                                                            fieldsQuery.isError
                                                        }
                                                        onRetry={() =>
                                                            fieldsQuery.refetch()
                                                        }
                                                        currentPage={
                                                            currentPage
                                                        }
                                                        totalPages={totalPages}
                                                        onOpenInDocument={
                                                            setPdfModal
                                                        }
                                                    />
                                                </div>
                                            </>
                                        )}

                                    {/* Related scopes/comparisons are clause
                                      analysis artifacts (see supportsScopeAnalysis). */}
                                    {supportsScopeAnalysis && (
                                        <>
                                            <Separator className="mb-5 opacity-60" />
                                            <RelatedScopesSection
                                                isLoading={false}
                                                scopes={scopes}
                                            />
                                            <Separator className="mb-5 opacity-60" />
                                            <RelatedComparisonsSection
                                                isLoading={false}
                                                comparisons={comparisons}
                                            />
                                        </>
                                    )}
                                </>
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {isMobile ? (
                <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
                    {/*
                     * `data-[side=right]:w-full data-[side=right]:sm:max-w-none`
                     * is LOAD-BEARING. Without it the mobile Sheet defaults
                     * to a narrow drawer and the panel shell becomes too
                     * cramped to read on phones. The Sheet primitive does
                     * not yet expose a width prop, so this className override
                     * is the only mechanism. Do NOT drop it during MUI
                     * cleanup or shadcn version bumps.
                     */}
                    <SheetContent
                        side="right"
                        showCloseButton={false}
                        className="flex flex-col gap-0 p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-none"
                    >
                        <SheetTitle className="sr-only">
                            {doc?.document_name ?? "Document details"}
                        </SheetTitle>
                        {panelContent}
                    </SheetContent>
                </Sheet>
            ) : (
                panelContent
            )}

            {pdfModal !== null && isPdfModalOpen && (
                <PdfSourceModal
                    documentId={pdfModal.documentId}
                    propertyId={pdfModal.propertyId}
                    pageNumber={pdfModal.pageNumber}
                    fieldName={pdfModal.fieldName}
                    source={pdfModal.source}
                    onClose={() => setPdfModal(null)}
                />
            )}

            {/* Dialogs rendered outside panel */}
            {doc && isConfirmedOwn && (
                <>
                    <RenameDocumentDialog
                        open={isDialogOpen("rename")}
                        onClose={closeDialog}
                        document={doc}
                        onRenamed={closeDialog}
                    />
                    <MoveToFolderModal
                        open={isDialogOpen("move")}
                        onClose={closeDialog}
                        items={[libraryDocumentToListItem(doc)]}
                        onMoved={handleMoveComplete}
                    />
                    <DeleteDocumentDialog
                        open={isDialogOpen("delete")}
                        onClose={closeDialog}
                        document={toListItem(doc)}
                        onConfirm={() => deleteDocument(doc.id)}
                        isDeleting={isDeleting}
                    />
                </>
            )}
            {doc && isChatEnabled && isConfirmedOwn && (
                <ChatSheet
                    attachment={{
                        id: doc.id,
                        name: doc.document_name,
                        type: AttachmentType.Document,
                        file_type: doc.file_type,
                    }}
                    open={isDialogOpen("chat")}
                    onClose={closeDialog}
                />
            )}
        </>
    );
};

/* ─── Header ─────────────────────────────────────────────────────────── */

const PanelHeader = ({
    filename,
    isLoading,
    onRename,
    onClose,
    showRename,
}: {
    filename: string | undefined;
    isLoading: boolean;
    onRename: () => void;
    onClose: () => void;
    showRename: boolean;
}) => (
    <div className="border-border flex shrink-0 items-center gap-3 border-b px-4 py-2.5">
        <div className="bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-lg">
            <FileText className="size-4" strokeWidth={2.2} />
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-1">
            {isLoading ? (
                <Skeleton className="h-4 w-48" />
            ) : filename ? (
                <h2
                    title={filename}
                    className="line-clamp-2 text-sm leading-snug font-semibold"
                >
                    {filename}
                </h2>
            ) : null}
            {showRename && (
                <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Rename document"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={onRename}
                >
                    <Pencil className="size-3.5" />
                </Button>
            )}
        </div>
        <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Close panel"
            className="text-muted-foreground hover:text-foreground"
            onClick={onClose}
        >
            <X className="size-4" />
        </Button>
    </div>
);

/* ─── Meta strip ─────────────────────────────────────────────────────── */

const MetaBullet = () => (
    <span aria-hidden className="text-muted-foreground/40">
        ·
    </span>
);

const MetaStrip = ({
    folderName,
    isKnowledgeBase,
    sharedBadgeLabel,
    pageCount,
    clientName,
    createdAt,
    tags,
}: {
    folderName: string | null;
    isKnowledgeBase: boolean;
    sharedBadgeLabel: string | null;
    pageCount: number | null;
    clientName: string | null;
    createdAt: string;
    tags: { label: string; colorKey: keyof typeof TAG_PALETTE }[];
}) => {
    const FolderIcon = isKnowledgeBase ? BookOpen : Folder;
    // Bullets are separators between rendered sections. Collect the active
    // sections, then intercalate `·` so missing optional sections (no folder,
    // no client, no tags) don't leave a leading or doubled separator.
    const sections: { key: string; node: React.ReactNode }[] = [];
    if (folderName) {
        sections.push({
            key: "folder",
            node: (
                <span
                    data-testid="document-folder-chip"
                    className={cn(
                        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium",
                        isKnowledgeBase
                            ? "bg-primary/10 text-primary"
                            : "bg-muted text-foreground"
                    )}
                >
                    <FolderIcon className="size-3" strokeWidth={2.2} />
                    {folderName}
                </span>
            ),
        });
    }
    if (sharedBadgeLabel !== null) {
        sections.push({
            key: "shared",
            node: (
                <span
                    data-testid="document-shared-badge"
                    className="bg-primary/10 text-primary inline-flex min-w-0 items-center gap-1 rounded-full px-2 py-0.5 font-medium"
                >
                    <BookOpen className="size-3 shrink-0" strokeWidth={2.2} />
                    <span title={sharedBadgeLabel} className="truncate">
                        {sharedBadgeLabel}
                    </span>
                </span>
            ),
        });
    }
    if (pageCount !== null && pageCount !== undefined) {
        sections.push({
            key: "pages",
            node: (
                <>
                    <span>{pageCount}</span>
                    <span>{pageCount === 1 ? "page" : "pages"}</span>
                </>
            ),
        });
    }
    sections.push({
        key: "uploaded",
        node: (
            <span title={new Date(createdAt).toLocaleString()}>
                {formatDistanceToNowStrict(new Date(createdAt), {
                    addSuffix: true,
                })}
            </span>
        ),
    });
    if (clientName) {
        sections.push({ key: "client", node: <span>{clientName}</span> });
    }

    return (
        <div
            data-testid="document-meta-strip"
            className="text-muted-foreground mb-5 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs"
        >
            {sections.map((section, i) => (
                <Fragment key={section.key}>
                    {i > 0 && <MetaBullet />}
                    {section.node}
                </Fragment>
            ))}
            {tags.length > 0 && <MetaBullet />}
            {tags.map((tag) => (
                <span
                    key={tag.colorKey}
                    className={cn(
                        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium",
                        TAG_PALETTE[tag.colorKey]
                    )}
                >
                    {tag.label}
                </span>
            ))}
        </div>
    );
};

/* ─── Primary + secondary actions ─────────────────────────────────────── */

const PanelActions = ({
    supportsScopeAnalysis,
    isChatEnabled,
    canManage,
    isDocumentProcessed,
    isViewing,
    isSpreadsheet,
    onRunScope,
    onAskPolly,
    onView,
    onMove,
    onDelete,
}: {
    supportsScopeAnalysis: boolean;
    isChatEnabled: boolean;
    canManage: boolean;
    isDocumentProcessed: boolean;
    isViewing: boolean;
    isSpreadsheet: boolean;
    onRunScope: () => void;
    onAskPolly: () => void;
    onView: () => void;
    onMove: () => void;
    onDelete: () => void;
}) => (
    <div className="mb-2 flex flex-col gap-2">
        {/* Contract-only: scope analysis runs clause extraction against a
          checklist, N/A for spreadsheets/KB (see supportsScopeAnalysis). */}
        {supportsScopeAnalysis && (
            <Button
                variant="default"
                className="bg-primary text-primary-foreground hover:bg-primary/90 w-full text-sm font-semibold shadow-sm"
                onClick={onRunScope}
            >
                <ScanSearch className="size-4" strokeWidth={2.2} />
                Run Scope
            </Button>
        )}
        <div className="flex gap-2">
            {isChatEnabled && (
                <PollyButton
                    isDocumentProcessed={isDocumentProcessed}
                    onAskPolly={onAskPolly}
                />
            )}
            <Button
                variant="outline"
                size="sm"
                className="text-muted-foreground hover:text-foreground flex-1"
                onClick={onView}
                disabled={isViewing}
                aria-label={isSpreadsheet ? "Download" : "View"}
                title={isSpreadsheet ? "Download" : "View"}
            >
                {isSpreadsheet ? (
                    <Download className="size-3.5" strokeWidth={2.2} />
                ) : (
                    <Eye className="size-3.5" strokeWidth={2.2} />
                )}
                <span className="@max-md:hidden">
                    {isSpreadsheet ? "Download" : "View"}
                </span>
            </Button>
            {canManage && (
                <>
                    <Button
                        variant="outline"
                        size="sm"
                        className="text-muted-foreground hover:text-foreground flex-1"
                        onClick={onMove}
                        aria-label="Move"
                        title="Move"
                    >
                        <FolderInput className="size-3.5" strokeWidth={2.2} />
                        <span className="@max-md:hidden">Move</span>
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        className="text-muted-foreground hover:border-destructive/30 hover:bg-destructive/5 hover:text-destructive flex-1"
                        onClick={onDelete}
                        aria-label="Delete"
                        title="Delete"
                    >
                        <Trash2 className="size-3.5" strokeWidth={2.2} />
                        <span className="@max-md:hidden">Delete</span>
                    </Button>
                </>
            )}
        </div>
    </div>
);

const PollyButton = ({
    isDocumentProcessed,
    onAskPolly,
}: {
    isDocumentProcessed: boolean;
    onAskPolly: () => void;
}) => {
    const button = (
        <Button
            variant="outline"
            size="sm"
            disabled={!isDocumentProcessed}
            onClick={onAskPolly}
            aria-label="Ask Polly"
            className="text-copper hover:bg-copper/5 hover:text-copper w-full"
        >
            <MessageSquare className="size-3.5" strokeWidth={2.2} />
            <span className="@max-md:hidden">Ask Polly</span>
        </Button>
    );

    if (!isDocumentProcessed) {
        return (
            <Tooltip
                title="Processing in progress"
                description="Chat is available after the document has finished processing"
            >
                <span className="flex flex-1">{button}</span>
            </Tooltip>
        );
    }
    return <span className="flex flex-1">{button}</span>;
};

/* ─── Loading + error states ──────────────────────────────────────────── */

const LoadingSkeleton = () => (
    <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
            <Skeleton className="h-5 w-28 rounded-full" />
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-5 w-16 rounded-full" />
        </div>
        <Skeleton className="h-9 w-full rounded-lg" />
        <div className="flex gap-2">
            <Skeleton className="h-7 flex-1 rounded-lg" />
            <Skeleton className="h-7 flex-1 rounded-lg" />
            <Skeleton className="h-7 flex-1 rounded-lg" />
        </div>
        <Separator className="opacity-60" />
        <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-5 rounded" />
        </div>
        <div className="flex flex-col gap-1.5">
            <Skeleton className="h-14 w-full rounded-xl" />
            <Skeleton className="h-14 w-full rounded-xl" />
        </div>
        <Separator className="opacity-60" />
        <Skeleton className="h-14 w-full rounded-xl" />
    </div>
);

const PanelErrorState = ({ onRetry }: { onRetry: () => void }) => (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
        <TriangleAlert
            className="text-muted-foreground size-8"
            strokeWidth={1.8}
        />
        <div className="space-y-1">
            <div className="text-sm font-semibold">Couldn't load document</div>
            <div className="text-muted-foreground text-xs">
                Check your connection and try again.
            </div>
        </div>
        <Button variant="outline" size="sm" onClick={onRetry}>
            Try again
        </Button>
    </div>
);
